import React from "react";
import type { ApolloClient } from "@apollo/client";
import * as yup from "yup";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Formik } from "formik";
import type { FormikProps } from "formik";
import { Card, Col, Form, Row } from "react-bootstrap";
import { get, isEmpty, map } from "lodash";
import { toast } from "react-toastify";
import { FiChevronLeft, FiChevronRight, FiGrid } from "react-icons/fi";
import { uploadFile } from "statics/rosterSteps";
import { uploadEmployeesDocument, uploadRoster } from "actions/employerActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants, employerConstants } from "actions/types";
import { isIconAdminSelector } from "store/selectors/user";
import {
  getEmployerGroupsByCompanyId,
  uploadEmployeesDocumentForEmployer,
} from "actions/adminActions";

import RosterUploader from "components/RosterUploader";
import LottieAnimation from "components/LottieAnimation";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import IconMessageCard from "components/IconMessageCard";
import IconSpinner from "components/IconSpinner";
import Button from "components/Button";
import Alert from "components/Alert";

import "./AddEmployeeUploadForm.scss";
import { ScrollToFieldError } from "utils/form";
import type { RootState } from "types/state";
import type { UploadRosterMutationVariables } from "types/graphql";

const schema = yup.object({
  groupId: yup.string().required(),
});

const isLoadingSelector = createLoadingSelector(
  employerConstants.UPLOAD_ROSTER
);

const isLoadingEmployerGroupsSelector = createLoadingSelector(
  adminConstants.GET_GROUPS_BY_COMPANY_ID
);

const errorSelector = createErrorSelector(employerConstants.UPLOAD_ROSTER);

const mapStateToProps = (state: RootState) => {
  return {
    groups: state.employer.groups,
    error: errorSelector(state),
    isLoading: isLoadingSelector(state),
    document: get(state, `userDocuments.documents[0]`),
    isIconAdmin: isIconAdminSelector(state),
    isFetchingEmployerGroups: isLoadingEmployerGroupsSelector(state),
  };
};

const mapDispatchToProps = {
  uploadEmployeesDocument,
  getEmployerGroupsByCompanyId,
  uploadEmployeesDocumentForEmployer,
  uploadRoster,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

/** Caller-supplied props: embedded by the employer user-management page. */
interface OwnProps {
  companyId: string;
  // also handed to RosterUploader's setActiveStep ((...args: unknown[]) => void)
  onStepChange: (...args: unknown[]) => void;
  onClose?: () => void;
  hideForm: () => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  submittedDocument: boolean;
  companies: unknown[];
  selectedCompanyId: string;
  selectedGroupId: string;
  initialFetching: boolean;
  showUploadToggle: boolean;
  showRosterUploader: boolean;
  groupsByCompanyId: unknown[];
  /** read in _renderUpload but never written by any setState — pre-existing */
  isFetchingCompanies?: boolean;
}

/** Formik values — mirrors initialValues: State spread plus groupId. */
interface FormValues extends State {
  groupId: string;
}

class AddEmployeeUploadForm extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      submittedDocument: false,
      companies: [],
      selectedCompanyId: "",
      // lodash get returns unknown; selectedGroupId is consumed as a string
      selectedGroupId: get(props, "groups[0].id") as string,
      initialFetching: props.isIconAdmin,
      showUploadToggle: false,
      showRosterUploader: false,
      groupsByCompanyId: [],
    };
  }

  async componentDidMount() {
    if (this.props.isIconAdmin) {
      await this.props.getEmployerGroupsByCompanyId(
        this.props.client as ApolloClient<object>,
        this.props.companyId
      );

      this.setState({
        initialFetching: false,
        selectedGroupId: get(this.props, "groups[0].id") as string,
      });
    }
  }

  _buildGroupInput(
    values: FormValues,
    handleChange: FormikProps<FormValues>["handleChange"],
    handleBlur: FormikProps<FormValues>["handleBlur"],
    touched: FormikProps<FormValues>["touched"],
    errors: FormikProps<FormValues>["errors"]
  ) {
    if (isEmpty(this.props.groups)) {
      return (
        <IconMessageCard
          header={"There are no groups for this employer"}
          icon={<FiGrid color="white" stroke="#60A4BF" size={16} />}
        />
      );
    }
    return (
      <div className="group-select">
        <Row className="mb-3">
          <Form.Group as={Col} sm={6} controlId="formBasicGroupId">
            <Form.Label>Group</Form.Label>
            <Form.Select
              name="groupId"
              value={values.groupId}
              onChange={(event) => {
                handleChange(event);
                this.setState({ selectedGroupId: event.target.value });
              }}
              onBlur={handleBlur}
              isInvalid={touched.groupId && !!errors.groupId}
              isValid={touched.groupId && !errors.groupId}
            >
              {map(
                this.props.groups as Array<{
                  id?: string;
                  name?: string;
                  planType?: string;
                }>,
                (group) => (
                  <option key={group.id} value={group.id}>
                    {this.props.isIconAdmin
                      ? `${group.name} - ${group.planType} - ${group.id}`
                      : group.name}
                  </option>
                )
              )}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.groupId}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
      </div>
    );
  }

  _onRosterUpload = async (data: unknown) => {
    if (isEmpty(data)) {
      toast.error(
        "Failed to upload roster. You must upload at least one employee."
      );
      this.props.onStepChange(uploadFile);
      return;
    }
    const result = await this.props.uploadRoster(
      this.props.client as ApolloClient<object>,
      {
        rows: data as UploadRosterMutationVariables["rows"],
        groupId: this.state.selectedGroupId,
        companyId: this.props.companyId,
      }
    );
    if (result.success) {
      toast.success("Successfully uploaded document!");
      this.props.onClose && this.props.onClose();
    } else {
      this.props.onStepChange(uploadFile);
    }
  };

  _renderUpload = () => {
    if (this.state.isFetchingCompanies) {
      return <IconSpinner centered />;
    }

    return (
      <div className="download-employee-template">
        <div className="main-content">
          <Row>
            <Col as={Col} sm={9}>
              <Card>
                {this.props.isLoading && (
                  <>
                    <Card.Title>
                      <p className="page-header">Processing File</p>
                      <p className="page-subtext">
                        Give us a moment while we process your file.{" "}
                      </p>
                    </Card.Title>
                    <Card.Body>
                      <LottieAnimation />
                    </Card.Body>
                  </>
                )}
                {!this.state.showRosterUploader && !this.props.isLoading && (
                  <>
                    <Card.Title>
                      <IconHeader
                        variant="cardHeader"
                        headerText="Select a group"
                      />
                      <IconSubheader subheader="This is the group you will be uploading the roster to." />
                    </Card.Title>
                    <Card.Body>
                      <Formik<FormValues>
                        validateOnChange={false}
                        validationSchema={schema}
                        // @ts-expect-error _uploadFile is not defined on this
                        // class — pre-existing bug; onSubmit resolves to
                        // undefined. Preserved exactly per conversion rules.
                        onSubmit={this._uploadFile}
                        enableReinitialize={true}
                        initialValues={{
                          ...this.state,
                          groupId: this.state.selectedGroupId,
                        }}
                      >
                        {({
                          handleSubmit,
                          handleChange,
                          values,
                          touched,
                          errors,
                          handleBlur,
                        }: FormikProps<FormValues>) => (
                          <Form noValidate onSubmit={handleSubmit}>
                            <ScrollToFieldError />
                            {this.props.isIconAdmin &&
                            this.props.isFetchingEmployerGroups ? (
                              <IconSpinner centered />
                            ) : (
                              this._buildGroupInput(
                                values,
                                handleChange,
                                handleBlur,
                                touched,
                                errors
                              )
                            )}
                            {this.props.error && (
                              <Alert type="error" msg={this.props.error} />
                            )}
                            <div className="btn-row">
                              {!this.state.submittedDocument && (
                                <Button
                                  icon={{
                                    icon: (
                                      <FiChevronLeft
                                        size={14}
                                        stroke={"#0cb7c4"}
                                      />
                                    ),
                                    position: "left",
                                  }}
                                  btnLabel="Go Back"
                                  name="cancel"
                                  color="cancel"
                                  onClick={this.props.hideForm}
                                />
                              )}
                              <Button
                                btnLabel={"Upload Roster"}
                                name="submit"
                                onClick={() =>
                                  this.setState({ showRosterUploader: true })
                                }
                                disabled={isEmpty(this.props.groups)}
                                withArrow={this.props.isLoading}
                                loading={this.props.isLoading}
                                icon={{
                                  icon: (
                                    <FiChevronRight size={8} color={"#FFFFF"} />
                                  ),
                                  position: "right",
                                }}
                                type="button"
                              />
                            </div>
                          </Form>
                        )}
                      </Formik>
                    </Card.Body>
                  </>
                )}
                {this.state.showRosterUploader && (
                  <Card.Body>
                    <RosterUploader
                      onSubmit={this._onRosterUpload}
                      setActiveStep={this.props.onStepChange}
                      onClose={() =>
                        this.setState({ showRosterUploader: false })
                      }
                    />
                  </Card.Body>
                )}
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  render() {
    return (
      <div className="add-employee-form" style={{ marginBottom: 8 }}>
        {this._renderUpload()}
      </div>
    );
  }
}

export default connector(withApollo<Props>(AddEmployeeUploadForm));
